import {
    Box,
    Button,
    Divider,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    alpha,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useMemo, useRef, useState } from 'react';
import server from '../../../services/server';
import useErrors from '../../../hooks/useErrors';

function money(value) {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function computePreview(purchaseValue, offer) {
    const purchase = Number(String(purchaseValue).replace(',', '.'));
    if (!offer || !Number.isFinite(purchase) || purchase <= 0) {
        return null;
    }

    let discount = 0;
    if (offer.isPercentual) {
        discount = purchase * (Number(offer.value) / 100);
    } else {
        discount = Number(offer.value) || 0;
    }
    if (discount < 0) discount = 0;
    if (discount > purchase) discount = purchase;

    return {
        purchase,
        discount,
        amountToPay: Math.max(0, purchase - discount),
    };
}

export default function AdvertiserValidateCode() {
    const { setErrors, enqueueSnackbar } = useErrors();
    const [code, setCode] = useState('');
    const [purchaseValue, setPurchaseValue] = useState('');
    const [lookup, setLookup] = useState(null);
    const [lookingUp, setLookingUp] = useState(false);
    const [validating, setValidating] = useState(false);
    const [result, setResult] = useState(null);
    const codeRef = useRef(null);
    const purchaseRef = useRef(null);

    useEffect(() => {
        codeRef.current?.focus();
    }, []);

    const preview = useMemo(
        () => computePreview(purchaseValue, lookup?.offer),
        [purchaseValue, lookup],
    );

    const resetToCodeStep = () => {
        setLookup(null);
        setPurchaseValue('');
        setResult(null);
        requestAnimationFrame(() => codeRef.current?.focus());
    };

    const handleLookup = async event => {
        event?.preventDefault?.();
        const trimmed = code.trim();
        if (!trimmed) {
            enqueueSnackbar('Informe o código do resgate.', { variant: 'warning' });
            return;
        }
        try {
            setLookingUp(true);
            setResult(null);
            const { data } = await server.get('/advertiser/redemptions/lookup', {
                params: { code: trimmed },
            });
            setLookup(data.data);
            setPurchaseValue('');
            requestAnimationFrame(() => purchaseRef.current?.focus());
        } catch (error) {
            setLookup(null);
            setErrors(error);
        } finally {
            setLookingUp(false);
        }
    };

    const handleValidate = async event => {
        event.preventDefault();
        if (!lookup) return;

        const purchase = Number(String(purchaseValue).replace(',', '.'));
        if (!Number.isFinite(purchase) || purchase <= 0) {
            enqueueSnackbar('Informe o valor da compra.', { variant: 'warning' });
            return;
        }

        try {
            setValidating(true);
            const { data } = await server.post('/advertiser/redemptions/validate', {
                code: lookup.code || code.trim(),
                purchaseValue: purchase,
            });
            setResult(data.data);
            setLookup(null);
            setCode('');
            setPurchaseValue('');
            enqueueSnackbar('Resgate validado com sucesso.', { variant: 'success' });
            requestAnimationFrame(() => codeRef.current?.focus());
        } catch (error) {
            setErrors(error);
        } finally {
            setValidating(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: { xs: 'calc(100vh - 140px)', md: 'calc(100vh - 160px)' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                py: { xs: 3, md: 4 },
                background: theme =>
                    `radial-gradient(ellipse 80% 60% at 50% 35%, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%),
                     linear-gradient(180deg, ${theme.palette.grey[50]} 0%, ${theme.palette.background.default} 100%)`,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 460,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    bgcolor: 'background.paper',
                    boxShadow: theme => `0 12px 40px ${alpha(theme.palette.common.black, 0.08)}`,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        px: 3,
                        py: 2.5,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        textAlign: 'center',
                    }}
                >
                    <QrCodeScannerIcon sx={{ fontSize: 36, mb: 0.5, opacity: 0.95 }} />
                    <Typography variant="h5" fontWeight={700} letterSpacing={0.3}>
                        Validar código
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                        {lookup ? 'Confira a oferta e informe o valor da compra' : '1º passo: busque o código do cliente'}
                    </Typography>
                </Box>

                <Stack spacing={2.5} sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                    <Box component="form" onSubmit={handleLookup}>
                        <Stack spacing={1.5}>
                            <TextField
                                inputRef={codeRef}
                                fullWidth
                                autoComplete="off"
                                label="Código do resgate"
                                placeholder="Ex.: A1B2C3D4"
                                value={code}
                                onChange={event => {
                                    setCode(event.target.value.toUpperCase());
                                    if (lookup || result) {
                                        setLookup(null);
                                        setPurchaseValue('');
                                        setResult(null);
                                    }
                                }}
                                disabled={Boolean(lookup)}
                                inputProps={{
                                    style: {
                                        fontSize: '1.5rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.18em',
                                        textAlign: 'center',
                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                    },
                                    spellCheck: false,
                                }}
                                sx={{ '& .MuiInputBase-root': { py: 0.5 } }}
                            />
                            {!lookup ? (
                                <LoadingButton
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    loading={lookingUp}
                                    fullWidth
                                    startIcon={<SearchIcon />}
                                    sx={{ py: 1.4, fontWeight: 700, textTransform: 'none' }}
                                >
                                    Buscar código
                                </LoadingButton>
                            ) : (
                                <Button variant="text" onClick={resetToCodeStep} sx={{ textTransform: 'none' }}>
                                    Trocar código
                                </Button>
                            )}
                        </Stack>
                    </Box>

                    {lookup ? (
                        <Box component="form" onSubmit={handleValidate}>
                            <Stack spacing={2}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
                                        border: '1px solid',
                                        borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                                    }}
                                >
                                    <Typography variant="overline" color="text.secondary">
                                        Oferta encontrada
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        {lookup.offer?.name || 'Oferta'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {lookup.offer?.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                                        Código {lookup.code}
                                    </Typography>
                                </Box>

                                <TextField
                                    inputRef={purchaseRef}
                                    fullWidth
                                    label="Valor da compra"
                                    placeholder="0,00"
                                    value={purchaseValue}
                                    onChange={event => setPurchaseValue(event.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography fontWeight={700} color="text.secondary">
                                                    R$
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{
                                        inputMode: 'decimal',
                                        style: { fontSize: '1.35rem', fontWeight: 600 },
                                    }}
                                />

                                {preview ? (
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: 'grey.50',
                                        }}
                                    >
                                        <Stack spacing={1}>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography variant="body2" color="text.secondary">
                                                    Valor da compra
                                                </Typography>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {money(preview.purchase)}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography variant="body2" color="text.secondary">
                                                    Desconto (descontômetro)
                                                </Typography>
                                                <Typography variant="body2" fontWeight={700} color="success.main">
                                                    − {money(preview.discount)}
                                                </Typography>
                                            </Stack>
                                            <Divider />
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="subtitle2" fontWeight={700}>
                                                    Cliente paga
                                                </Typography>
                                                <Typography variant="h6" fontWeight={800}>
                                                    {money(preview.amountToPay)}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                ) : null}

                                <LoadingButton
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    loading={validating}
                                    fullWidth
                                    disabled={!preview}
                                    sx={{
                                        py: 1.6,
                                        fontSize: '1.05rem',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                    }}
                                >
                                    Confirmar validação
                                </LoadingButton>
                            </Stack>
                        </Box>
                    ) : null}

                    {result ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.5,
                                p: 2,
                                borderRadius: 2,
                                bgcolor: theme => alpha(theme.palette.success.main, 0.1),
                                border: '1px solid',
                                borderColor: theme => alpha(theme.palette.success.main, 0.35),
                            }}
                        >
                            <CheckCircleOutlineIcon color="success" sx={{ mt: 0.2 }} />
                            <Box>
                                <Typography variant="subtitle1" fontWeight={700} color="success.dark">
                                    Código validado
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Compra: {money(result.saleValue)} · Desconto:{' '}
                                    {money(result.discount ?? result.subscriberVoucherValue)} · Cliente pagou:{' '}
                                    {money(result.amountToPay ?? result.validatedAmount)}
                                </Typography>
                            </Box>
                        </Box>
                    ) : !lookup ? (
                        <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ lineHeight: 1.5 }}>
                            Busque o código primeiro. Depois informe o valor da compra para ver o desconto e confirmar.
                        </Typography>
                    ) : null}
                </Stack>
            </Box>
        </Box>
    );
}
